import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import MainContentContainer from '@/components/layouts/MainContentContainer'
import { MarketProgramFilterTabs } from '@/features/site/components/availability/MarketProgramFilterTabs'
import { RestrictionsDataGrid } from '@/features/site/components/availability/restriction/RestrictionsDataGrid'
import { ServiceRulesDataGrid } from '@/features/site/components/availability/serviceRule/ServiceRulesDataGrid'
import { NoMarketProgram } from '@/features/site/components/NoMarketProgram'
import type { MarketProgram } from '@/features/site/types/marketProgram'
import type { Site, SiteMarketProgramContract } from '@/features/site/types/site'
import { compareMarketPrograms } from '@/features/site/utils/sortMarketPrograms'
import type { MarketProgramType } from '@/types/marketProgramType'

type SiteUnavailabilityViewProps = {
  site: Site
}

export const SiteUnavailabilityView = ({ site }: SiteUnavailabilityViewProps) => {
  const { t } = useTranslation()
  const [selectedMarketProgram, setSelectedMarketProgram] = useState<SiteMarketProgramContract>()

  const sortedMarketPrograms =
    site.marketProgramContracts?.sort((a, b) => {
      return compareMarketPrograms(t, a.marketProgram!, b.marketProgram!)
    }) ?? []

  if (sortedMarketPrograms.length === 0) {
    return <NoMarketProgram />
  }

  const siteMarketProgramTypes = sortedMarketPrograms.map(
    (contract) => contract.marketProgram?.type as MarketProgramType,
  )

  const marketProgramContract = selectedMarketProgram ?? sortedMarketPrograms[0]
  const marketProgramType = selectedMarketProgram?.marketProgram?.type ?? sortedMarketPrograms[0].marketProgram?.type

  const handleChange = (newValue: MarketProgramType) => {
    setSelectedMarketProgram(sortedMarketPrograms.find((contract) => contract.marketProgram?.type === newValue))
  }

  return (
    <MainContentContainer>
      <MarketProgramFilterTabs
        marketPrograms={siteMarketProgramTypes}
        value={marketProgramType}
        onChange={handleChange}
      />
      {isMarketProgramAvailability(marketProgramContract.marketProgram!) ? (
        <ServiceRulesDataGrid
          selectedMarketProgram={marketProgramContract.marketProgram!}
          siteLocation={site.location ?? ''}
          siteTimezone={site.timezone ?? ''}
          siteUuid={site.uuid ?? ''}
        />
      ) : (
        <RestrictionsDataGrid
          selectedMarketProgram={marketProgramContract.marketProgram!}
          siteLocation={site.location ?? ''}
          siteTimezone={site.timezone ?? ''}
          siteUuid={site.uuid ?? ''}
        />
      )}
    </MainContentContainer>
  )
}

// In the initial implementation, additional check "|| !program.isReserve" was present here.
// This property is not present in the current response from backend. Re-add if needed.
function isMarketProgramAvailability(program: MarketProgram) {
  return program.type === 'afrr-up' || program.type === 'afrr-down'
}
